import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ConfirmationDialog,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Download,
  Info,
  Loader2,
  RefreshCw,
  Save,
  Table2,
  X,
} from 'lucide-react';
import { useStore } from '../store';
import { FeatureFlag } from '../constants/featureFlags';
import ResultsTable from '../components/reporting/ResultsTable';
import ResultsChart from '../components/reporting/ResultsChart';
import ReportScopeNotice from '../components/reporting/ReportScopeNotice';
import { ReportPopulationControls } from '../components/reporting/ReportPopulationControls';
import { hasNoClinicAccess } from '../components/reporting/reportScope';
import FieldPanel from '../components/reporting/FieldPanel';
import FiltersEditor from '../components/reporting/FiltersEditor';
import SortEditor from '../components/reporting/SortEditor';
import SaveReportDialog from '../components/reporting/SaveReportDialog';
import {
  addJoinedDataset,
  backfillJoinedDatasets,
  cycleSort,
  hasDefinitionWork,
  populationSettingEnabled,
  removeJoinedDataset,
  reorderColumns,
  setPopulationSetting,
  setView,
} from '../reporting/definitionUpdates';
import { exportRowsToXlsx } from '../reporting/exportExcel';
import { dimensionFormats, humanizeHeader } from '../reporting/formatValue';
import {
  createReportDefinition,
  getReportingManifest,
  listReportDefinitions,
  updateReportDefinition,
} from '../services/reportingApi';
import {
  referencedRelatedKeys,
  relatedDatasetsFor,
  stampDefinitionVersions,
} from '../reporting/columnRefs';
import {
  DETAIL_ROW_LIMIT,
  validateDefinition,
} from '../reporting/compileReport';
import {
  detailChartDefinition,
  splitDetailChartResult,
  validChartConfig,
} from '../reporting/detailChart';
import { MAX_CATEGORIES } from '../reporting/chartFormat';
import {
  type EffectiveWindow,
  useReportRunner,
} from '../reporting/useReportRunner';
// Dev-only: exposes window.__joinBench for the QA performance measurement
// (no-op in production builds).
import '../reporting/joinBench';
import type {
  DatasetColumn,
  DatasetManifestEntry,
  ReportDefinition,
  ReportListItem,
  ReportingManifest,
} from '../types/reporting';
import { emptyDefinition, SUPPORTED_SCHEMA_VERSION } from '../types/reporting';
import {
  formatDateInPT,
  formatDateTimeInPT,
  formatDateToMMDDYYYY,
  formatTimeInPT,
  msUntilNextPTMidnight,
} from '../utils/dateUtils';

// "Data as of" freshness label: Pacific wall-clock time, with the date
// prefixed only when the extract was built on a different Pacific calendar
// day (a tab left open overnight must not show an ambiguous bare time).
// `todayPT` is the current Pacific day (MM/dd/yyyy) supplied by the caller so
// the comparison re-evaluates when the day rolls over rather than being frozen
// at the render that first mounted the page.
const dataAsOfLabel = (builtAt: string, todayPT: string): string =>
  formatDateInPT(builtAt) === todayPT
    ? formatTimeInPT(builtAt)
    : formatDateTimeInPT(builtAt);

// The date window the extract was actually fetched with (Epic 5.2). A range
// restriction the user can't see is a silent data restriction — SCALING-DESIGN
// §8 calls that out by name — so whenever the runner narrows the fetch to an
// axis, name the axis and the bounds. Absent (null) means the whole dataset was
// fetched and there is nothing to disclose.
const effectiveWindowLabel = (window: EffectiveWindow): string => {
  const { axisLabel, lower, upper } = window;
  const from = lower ? formatDateToMMDDYYYY(lower) : null;
  const to = upper ? formatDateToMMDDYYYY(upper) : null;
  if (from && to) return `${axisLabel} ${from} – ${to}`;
  if (from) return `${axisLabel} from ${from}`;
  return `${axisLabel} through ${to}`;
};

// Truth-in-labeling caption for a DETAIL report's Chart tab. The table beside it
// is capped at DETAIL_ROW_LIMIT rows, so the chart's scope has to be stated
// rather than assumed: it aggregates every matching claim in the fetched window
// (not just the visible rows) but DISPLAYS at most MAX_CATEGORIES categories,
// because the compiled chart query is top-N in SQL.
const DETAIL_CHART_CAPTION = `Calculated from every matching claim in this window; up to ${MAX_CATEGORIES} categories shown.`;

// Collapsible sidebar section mirroring the design-system CollapsibleSection's
// markup/classes, but with a header slot so the live count renders inside a
// small solid-purple count badge instead of appended plain text. Built from
// the exported Collapsible primitives because CollapsibleSection types `title`
// as a plain string with no room for the chip; kept class-for-class identical
// so the four reporting sections don't visually drift from the rest of the app.
const SidebarSection = ({
  title,
  count,
  defaultOpen = true,
  children,
}: {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <Collapsible
      className='rounded-lg border'
      onOpenChange={setIsOpen}
      defaultOpen={defaultOpen}
    >
      <CollapsibleTrigger className='flex w-full items-center justify-between px-4 py-2.5 transition-colors hover:bg-gray-50'>
        <span className='flex items-center gap-1.5'>
          <h3 className='text-sm font-semibold text-gray-900'>{title}</h3>
          {!!count && count > 0 && (
            <span className='inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded bg-primary-600 px-1 text-xs font-medium leading-none tabular-nums text-primary-foreground'>
              {count}
            </span>
          )}
        </span>
        {isOpen ? (
          <ChevronUp className='h-4 w-4 text-gray-700' />
        ) : (
          <ChevronDown className='h-4 w-4 text-gray-700' />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className='border-t px-4 py-3'>
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

const ReportBuilder = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const showError = useStore(state => state.showError);
  const showSuccess = useStore(state => state.showSuccess);
  const user = useStore(state => state.user);
  // Subscribe to the DERIVED boolean, not the stable hasFeatureFlag fn: the fn
  // ref never changes when feature_flags change, so a mid-session flag grant
  // (org rollout while the tab stays open) must re-render — and re-run the load
  // effect — through the boolean flipping, not a new fn identity.
  const advancedReporting = useStore(state =>
    state.hasFeatureFlag(FeatureFlag.ADVANCED_REPORTING, 'workos')
  );
  // Focus target for the Data select so a fresh builder lands the cursor on the
  // first action (choosing a dataset) instead of an empty page.
  const dataSelectRef = useRef<HTMLButtonElement>(null);
  // The URL identity (id / system / dataset) whose load already landed in
  // state, so the loader can tell a real navigation from the save flow
  // stamping its own new id into the URL. See the load effect below.
  const loadedKeyRef = useRef<string | null>(null);
  const [manifest, setManifest] = useState<ReportingManifest | null>(null);
  const [sourceReport, setSourceReport] = useState<ReportListItem | null>(null);
  const [definition, setDefinition] = useState<ReportDefinition | null>(null);
  const [droppedColumns, setDroppedColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveDialog, setSaveDialog] = useState<'save' | 'copy' | null>(null);
  const [pendingDataset, setPendingDataset] = useState<string | null>(null);
  // Current Pacific calendar day, refreshed at each Pacific midnight so the
  // "Data as of" label flips a dateless bare time to a date-prefixed one when a
  // tab is left open overnight (nothing else forces a render at the boundary).
  const [todayPT, setTodayPT] = useState(() =>
    formatDateInPT(new Date().toISOString())
  );

  const ownsSource =
    sourceReport?.source === 'agency' && sourceReport.created_by === user?.id;
  // "Save a copy" only when opening a report the user can't overwrite (a
  // standard report, or someone else's agency report). A brand-new or
  // user-owned report saves in place.
  const isCopy = !!sourceReport && !ownsSource;

  const dataset: DatasetManifestEntry | null = useMemo(
    () =>
      manifest?.datasets.find(d => d.key === definition?.dataset_key) ?? null,
    [manifest, definition]
  );

  const relatedMap = useMemo(
    () => relatedDatasetsFor(dataset, manifest),
    [dataset, manifest]
  );

  // The related datasets EXPLICITLY joined into the base (definition
  // .joined_datasets), resolved to their manifests in join order. This gated
  // set — not the full permitted set — is handed to the field/filter pickers,
  // so a related dataset's columns become selectable only after the user adds
  // it as a join (mirroring "Add filter"), never as scattered per-column entry
  // points. The runner still receives the full relatedMap (it loads/validates
  // every joinable extract itself).
  const joinedRelated = useMemo(() => {
    const map = new Map<string, DatasetManifestEntry>();
    for (const key of definition?.joined_datasets ?? []) {
      const entry = relatedMap.get(key);
      if (entry) map.set(key, entry);
    }
    return map;
  }, [definition, relatedMap]);

  // Permitted related datasets not yet joined — the "+ Add dataset" options.
  const joinableDatasets = useMemo(() => {
    const joined = new Set(definition?.joined_datasets ?? []);
    return [...relatedMap.values()].filter(entry => !joined.has(entry.key));
  }, [definition, relatedMap]);

  useEffect(() => {
    // A flag-off direct-URL/bookmark visit must not fire reporting fetches (a
    // guaranteed 404 + error toast on a page the render guard already denies).
    // Stop before any request — and before claiming the key below, so a flag
    // granted mid-session still loads even though the URL never changed.
    if (!advancedReporting) {
      setLoading(false);
      return;
    }
    // Tear the builder down before each load so a failed one can't leave the
    // PREVIOUS report on screen under the new URL. Every early return below is
    // a toast-only path (not found / no data access / too-new schema): without
    // this reset, navigating from report A to an inaccessible B shows B's URL,
    // B's error toast, and A's fields — and "Save report" would then silently
    // overwrite A while the user believes they are editing B.
    //
    // Skipped when the URL already names the loaded report, because the
    // create-save below stamps its new id into the URL via navigate(replace).
    // That is a self-navigation to the report already on screen; reloading it
    // would blink the editor and re-run the query for no new information.
    const requested = searchParams.get('id')
      ? `id:${searchParams.get('id')}`
      : searchParams.get('system')
        ? `system:${searchParams.get('system')}`
        : searchParams.get('dataset')
          ? `dataset:${searchParams.get('dataset')}`
          : 'new';
    if (loadedKeyRef.current === requested) return;
    loadedKeyRef.current = requested;
    setSourceReport(null);
    setDefinition(null);
    setDroppedColumns([]);
    setLoading(true);

    let cancelled = false;
    // Whether this run's load reached a terminal state (data, toast, or error)
    // and therefore genuinely owns `loadedKeyRef`. See the cleanup below.
    let settled = false;
    const load = async () => {
      try {
        const manifestData = await getReportingManifest();
        if (cancelled) return;
        setManifest(manifestData);

        const savedId = searchParams.get('id');
        const systemKey = searchParams.get('system');
        const datasetKey = searchParams.get('dataset');
        if (savedId || systemKey) {
          const reports = await listReportDefinitions();
          if (cancelled) return;
          const report = reports.find(r =>
            savedId ? r.id === savedId : r.system_key === systemKey
          );
          if (!report) {
            showError('Reports', 'Report not found.');
            return;
          }
          const reportDataset = manifestData.datasets.find(
            d => d.key === report.dataset_key
          );
          if (!reportDataset) {
            showError(
              'Reports',
              "You don't have access to this report's data."
            );
            return;
          }
          if (report.definition.schema_version > SUPPORTED_SCHEMA_VERSION) {
            // A future definition shape this bundle doesn't understand.
            // Refuse to open (instead of silently degrading and enabling a
            // destructive re-save): a refresh gets the newer bundle.
            showError(
              'Reports',
              'This report needs a newer version of Roger. Refresh your browser to open it.'
            );
            return;
          }
          const relatedForReport = relatedDatasetsFor(
            reportDataset,
            manifestData
          );
          const { definition: cleaned, dropped } = validateDefinition(
            report.definition,
            reportDataset,
            relatedForReport
          );
          setSourceReport(report);
          setDefinition(
            stampDefinitionVersions(
              // Backfill joined_datasets so a legacy report saved by the old
              // per-column UI (dotted refs, no explicit join list) opens with
              // its related datasets already joined — validate-and-degrade
              // drops any ref/join the caller can no longer access.
              backfillJoinedDatasets(
                { ...cleaned, dataset_version: reportDataset.version },
                relatedForReport
              ),
              relatedForReport
            )
          );
          setDroppedColumns(dropped);
        } else if (datasetKey) {
          const entry = manifestData.datasets.find(d => d.key === datasetKey);
          if (entry) setDefinition(emptyDefinition(entry.key, entry.version));
        }
      } catch (error) {
        console.error('Failed to load report builder', error);
        showError('Reports', 'Unable to load report data. Please try again.');
      } finally {
        if (!cancelled) {
          settled = true;
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
      // Release the key claim when this run is torn down MID-FLIGHT. The claim
      // exists to stop a reload, but a cancelled load never rendered anything —
      // so leaving the claim behind makes the next run early-return on the key
      // match, and because that return skips the `finally` above, `loading`
      // stays true forever and the page is stuck on its skeleton.
      //
      // This is not hypothetical: React StrictMode double-invokes effects in
      // dev, so mount -> cleanup -> mount hit it on EVERY builder load. Any
      // deps change during a load does the same in production.
      if (!settled) loadedKeyRef.current = null;
    };
  }, [searchParams, showError, advancedReporting]);

  // Auto-focus the Data select on a fresh builder (no dataset chosen yet) so the
  // first action is under the cursor. Skipped once a dataset is loaded so it
  // never steals focus mid-edit.
  useEffect(() => {
    if (!loading && !dataset) dataSelectRef.current?.focus();
  }, [loading, dataset]);

  useEffect(() => {
    // Re-arm at each Pacific midnight and re-read "today" so the day-relative
    // "Data as of" label doesn't get stuck on a stale, dateless time when the
    // tab sits idle across the boundary. Recursive setTimeout (not setInterval)
    // so each wait is recomputed from the true remaining time; the +1s cushion
    // keeps the fire safely inside the new Pacific day.
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      timer = setTimeout(() => {
        setTodayPT(formatDateInPT(new Date().toISOString()));
        schedule();
      }, msUntilNextPTMidnight() + 1000);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  // ── Chart-on-detail (design §2) ────────────────────────────────────────────
  // ONE predicate drives the Tabs strip, the Tabs selection, AND the render
  // branch, so the strip can never highlight a selection its content
  // contradicts. `validChart` shares chartConfigValid with validateDefinition,
  // so a config that would be dropped on open can never gate a tab open.
  const groupByCount = definition?.group_bys.length ?? 0;
  const validChart = useMemo(
    () =>
      !!definition &&
      !!dataset &&
      validChartConfig(definition, dataset, relatedMap),
    [definition, dataset, relatedMap]
  );
  const chartActive =
    !!definition &&
    definition.view === 'bar' &&
    (groupByCount > 0 || validChart);
  // A DETAIL report's Chart tab runs a synthetic grouped definition
  // (detailChartDefinition: filters verbatim, gap_fill stripped, sort
  // neutralized, top-N in SQL). SINGLE ACTIVE RUNNER keyed on the visible tab —
  // DuckDB access is serialized through one connection, so a second parallel
  // runner would only queue behind this one and could block the tab the user
  // switched back to. A tab switch is an ordinary definition change, absorbed by
  // the runner's existing stale-result guard: no cancellation machinery, no
  // preloading.
  const detailChartActive = chartActive && groupByCount === 0;
  const activeDefinition = useMemo(
    () =>
      detailChartActive && definition?.chart
        ? detailChartDefinition(definition)
        : definition,
    [definition, detailChartActive]
  );
  const result = useReportRunner(activeDefinition, dataset, relatedMap);

  // Open-time drift (validateDefinition) and run-time drift (the runner's
  // extract-schema gate) share one "removed columns" notice so a stale-extract
  // degrade is visible exactly like a manifest-level one.
  const allDroppedColumns = useMemo(
    () => [...new Set([...droppedColumns, ...(result.droppedColumns ?? [])])],
    [droppedColumns, result.droppedColumns]
  );

  const columnLabels = useMemo(() => {
    const labels = new Map<string, string>();
    dataset?.columns.forEach(c => labels.set(c.name, c.label));
    relatedMap.forEach((entry, key) => {
      entry.columns.forEach(c =>
        labels.set(`${key}.${c.name}`, `${entry.label} · ${c.label}`)
      );
    });
    return labels;
  }, [dataset, relatedMap]);

  const columnMeta = useMemo(() => {
    const meta = new Map<string, DatasetColumn>();
    dataset?.columns.forEach(c => meta.set(c.name, c));
    relatedMap.forEach((entry, key) => {
      entry.columns.forEach(c => meta.set(`${key}.${c.name}`, c));
    });
    return meta;
  }, [dataset, relatedMap]);

  const dimFormats = useMemo(
    () => dimensionFormats(definition?.group_bys ?? []),
    [definition]
  );

  // One plain-language sentence (accepted v1 LEFT-JOIN semantics, no "join"
  // vocabulary) whenever a related column is referenced: unmatched primary rows
  // show blanks, and filters on related columns exclude those rows.
  const joinNotice = useMemo(() => {
    if (!definition || !dataset) return null;
    const labels = [...referencedRelatedKeys(definition)]
      .map(key => relatedMap.get(key)?.label ?? key)
      .sort();
    if (labels.length === 0) return null;
    return `${dataset.label} rows without linked ${labels.join(' / ')} data show blank values; filters on those columns leave them out.`;
  }, [definition, dataset, relatedMap]);

  const applyDataset = (key: string) => {
    const entry = manifest?.datasets.find(d => d.key === key);
    if (!entry) return;
    setSourceReport(null);
    setDroppedColumns([]);
    setDefinition(emptyDefinition(entry.key, entry.version));
  };

  const selectDataset = (key: string) => {
    const entry = manifest?.datasets.find(d => d.key === key);
    if (!entry || entry.key === definition?.dataset_key) return;
    // A dataset switch discards the whole definition — confirm first so one
    // misclick can't silently wipe group-bys, measures, and filters.
    if (hasDefinitionWork(definition)) {
      setPendingDataset(key);
      return;
    }
    applyDataset(key);
  };

  if (!advancedReporting) {
    return (
      <div className='container mx-auto px-4 py-6 text-sm text-muted-foreground'>
        You don't have access to Reports. Please contact your administrator if
        you need access.
      </div>
    );
  }
  if (loading) return <Skeleton className='h-64 w-full' />;

  // The true "detail dump" result — compileReport's final `SELECT … LIMIT
  // DETAIL_ROW_LIMIT` branch, reached ONLY when there are no group-bys, no
  // measures, and no pivot. That branch alone emits raw dataset columns in
  // definition.columns order and is subject to the row limit, so it alone drives
  // the truncation notice and column reorder. Gating on group_bys.length===0
  // (a bare "detail view") is not enough: a zero-group-by pivot
  // ({group_bys:[], measures:[count], pivot_column:set}) satisfies that yet
  // compiles via the PIVOT branch (fires on pivot_column && measures>0
  // regardless of group_bys) into derived value columns — letting a header drag
  // write those non-dataset keys into definition.columns and silently corrupt
  // saved state.
  const isDetailResult =
    !!definition &&
    definition.group_bys.length === 0 &&
    definition.measures.length === 0 &&
    !definition.pivot_column;
  const truncated =
    result.status === 'ready' &&
    isDetailResult &&
    result.rows.length >= DETAIL_ROW_LIMIT;
  // Block export while a definition edit is re-running: the visible rows are
  // stale until the new result lands.
  const exportReady = result.status === 'ready' && !result.revalidating;
  const isPivotResult =
    !!definition?.pivot_column && definition.measures.length > 0;
  // Pivot column name drives humanized pivot value headers/series (claim_type →
  // "Medicare Part A") across the table, chart, and CSV export.
  const pivotColumnName = isPivotResult
    ? definition?.pivot_column?.column
    : undefined;
  // The authored chart config backing the CURRENTLY VISIBLE detail-chart tab
  // (null on the Table tab and on grouped reports).
  const detailChart =
    detailChartActive && definition?.chart ? definition.chart : null;
  // Split the category-total scalar out of the chart run before anything
  // downstream sees it: ResultsChart treats every non-dimension column as a
  // chartable series, so the total would otherwise be drawn as a bar (and land
  // in the export).
  const chartResult = detailChart
    ? splitDetailChartResult(result.columns, result.rows)
    : null;
  const visibleColumns = chartResult ? chartResult.columns : result.columns;
  const visibleRows = chartResult ? chartResult.rows : result.rows;
  const handleExport = async () => {
    const base = (sourceReport?.name ?? 'report').replace(/[^\w-]+/g, '_');
    const suffix = truncated ? `_first_${DETAIL_ROW_LIMIT}_rows` : '';
    try {
      // Export follows the VISIBLE tab: the chart tab downloads the chart run's
      // grouped rows (≤ MAX_CATEGORIES by construction, total column stripped),
      // the table tab downloads the detail rows exactly as before.
      await exportRowsToXlsx(
        `${base}${suffix}.xlsx`,
        visibleColumns,
        visibleRows,
        columnLabels,
        columnMeta,
        dimFormats,
        pivotColumnName
      );
    } catch (error) {
      console.error('Failed to export report', error);
      showError('Reports', 'Failed to download report. Please try again.');
    }
  };
  // Per-tab export label, but ONLY for a detail report that actually has a
  // chart tab — a plain detail/grouped report keeps its existing label.
  const exportLabel = truncated
    ? `Export first ${DETAIL_ROW_LIMIT.toLocaleString()} rows (.xlsx)`
    : detailChart
      ? 'Download chart data'
      : validChart
        ? 'Download table rows'
        : 'Download';
  // The Tabs strip is a VIEW SELECTOR, not a result renderer: it is deliberately
  // independent of run status. Under the single-runner design there is no "table
  // run" whose validity could be consulted while Chart is active, and unmounting
  // the strip on a failed chart query would dead-end the user on an error pane
  // with no route back to Table. This also keeps the strip rendered over a
  // grouped report's error pane (an intentional behavior change).
  const showToggle = !!definition && (groupByCount > 0 || validChart);

  // Which result columns the user may sort by (all sorting runs in DuckDB).
  // Detail and grouped views: every result column is a valid ORDER BY target.
  // Pivot: only the row-dimension columns — pivot-generated value columns aren't
  // addressable in definition.sort, so they stay non-sortable.
  // Detail-chart tab: the visible result is the presentation-derived chart run,
  // whose aliases are not addressable in definition.sort — so no sort targets
  // are offered (the sidebar section hides) rather than leaking chart aliases
  // into the saved definition.
  const sortableColumns = detailChartActive
    ? []
    : isPivotResult
      ? result.columns.filter(column =>
          definition?.group_bys.some(g => g.column === column)
        )
      : result.columns;

  // Shared display-name resolver for the Sort sidebar rows: the same humanized
  // header the table renders (label > pivot value > humanized alias), so the
  // sidebar list and the table headers name each column identically.
  const columnLabel = (column: string): string =>
    humanizeHeader(
      column,
      columnLabels,
      columnMeta,
      pivotColumnName ? { column: pivotColumnName } : undefined
    );

  return (
    <div className='space-y-4'>
      <Link
        to='/reports'
        className='inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='h-4 w-4' /> Reports
      </Link>
      <div>
        <h1 className='text-2xl font-bold'>
          {sourceReport ? sourceReport.name : 'New report'}
        </h1>
        {sourceReport?.source === 'agency' && sourceReport.created_by_name && (
          <p className='text-sm text-muted-foreground'>
            Saved by {sourceReport.created_by_name}
            {sourceReport.created_at
              ? ` · ${formatDateInPT(sourceReport.created_at)}`
              : ''}
          </p>
        )}
      </div>

      {allDroppedColumns.length > 0 && (
        <Alert>
          <AlertTriangle className='h-4 w-4' />
          <AlertDescription>
            This report referenced columns that are no longer available and were
            removed: {allDroppedColumns.join(', ')}.
          </AlertDescription>
        </Alert>
      )}

      {joinNotice && (
        <p className='text-xs text-muted-foreground'>{joinNotice}</p>
      )}

      {/* Two-row grid so the DATA micro-label lives in its own top row and the
          select + results command bar both start at row 2 with flush tops —
          at rest and when the two sticky elements pin at top-4. A flex row
          can't do this: the label would push the whole sidebar down. */}
      <div className='grid grid-cols-[20rem_minmax(0,1fr)] items-start gap-x-6'>
        <label className='col-start-1 row-start-1 pb-1 text-xs font-semibold uppercase text-muted-foreground'>
          Data
        </label>
        {/* Wide (20rem grid track) so two-field filters (op + value) and long
            clinical / payer labels don't truncate; sticky with its own scroll
            so a long config list doesn't push the results out of view. */}
        <aside className='sticky top-4 col-start-1 row-start-2 max-h-[calc(100vh-2rem)] space-y-4 overflow-y-auto'>
          <Select value={dataset?.key ?? ''} onValueChange={selectDataset}>
            <SelectTrigger ref={dataSelectRef}>
              <SelectValue placeholder='Choose data' />
            </SelectTrigger>
            <SelectContent>
              {manifest?.datasets.map(entry => (
                <SelectItem key={entry.key} value={entry.key}>
                  {entry.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {dataset && definition && relatedMap.size > 0 && (
            <SidebarSection
              title='Related data'
              count={(definition.joined_datasets ?? []).length}
            >
              <div className='space-y-1.5'>
                {(definition.joined_datasets ?? []).map(key => {
                  const label = relatedMap.get(key)?.label ?? key;
                  return (
                    <div
                      key={key}
                      className='flex items-center justify-between gap-1 rounded-md border py-0.5 pl-2 pr-0.5 text-sm'
                    >
                      <span className='truncate' title={label}>
                        {label}
                      </span>
                      <Button
                        size='sm'
                        variant='ghost'
                        aria-label={`Remove ${label} data`}
                        onClick={() =>
                          setDefinition(removeJoinedDataset(definition, key))
                        }
                      >
                        <X className='h-4 w-4' />
                      </Button>
                    </div>
                  );
                })}
                {joinableDatasets.length > 0 && (
                  <Select
                    value=''
                    onValueChange={key =>
                      setDefinition(
                        addJoinedDataset(definition, key, dataset, relatedMap)
                      )
                    }
                  >
                    <SelectTrigger selectSize='sm'>
                      <SelectValue placeholder='+ Add dataset' />
                    </SelectTrigger>
                    <SelectContent>
                      {joinableDatasets.map(entry => (
                        <SelectItem key={entry.key} value={entry.key}>
                          {entry.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </SidebarSection>
          )}
          {dataset && definition && (
            <SidebarSection
              title='Fields'
              count={definition.group_bys.length + definition.measures.length}
            >
              <FieldPanel
                dataset={dataset}
                definition={definition}
                related={joinedRelated}
                onChange={next => {
                  // First group-by needs a measure to aggregate with.
                  const withMeasure =
                    next.group_bys.length > 0 && next.measures.length === 0
                      ? { ...next, measures: [{ agg: 'count' as const }] }
                      : next;
                  setDefinition(withMeasure);
                }}
              />
            </SidebarSection>
          )}
          {dataset && definition && (
            <SidebarSection title='Filters' count={definition.filters.length}>
              <FiltersEditor
                dataset={dataset}
                definition={definition}
                related={joinedRelated}
                onChange={setDefinition}
              />
            </SidebarSection>
          )}
          {/* Multi-sort editor. Gated on a runnable result (sortableColumns is
              derived from the compiled columns) so no dead "Sort" section shows
              before anything runs. Reads/writes the SAME definition.sort as the
              table headers, keeping the two in lockstep; all ordering runs in
              DuckDB via the compiled ORDER BY. */}
          {dataset && definition && sortableColumns.length > 0 && (
            <SidebarSection title='Sort' count={definition.sort.length}>
              <SortEditor
                definition={definition}
                sortableColumns={sortableColumns}
                columnLabel={columnLabel}
                onChange={setDefinition}
              />
            </SidebarSection>
          )}
        </aside>

        <main className='col-start-2 row-start-2 min-w-0'>
          {/* One sticky command bar over the results: freshness / row count on
              the left, the Table/Chart segmented control in the center, and the
              Refresh / Download / Save actions on the right. Sticky (like the
              sidebar) so it stays pinned while the table scrolls under it. Shows
              once a dataset is chosen — the pure empty state renders onboarding
              instead. */}
          {definition && dataset && (
            <div className='sticky top-4 z-10 mb-3 flex items-center justify-between gap-2 rounded-lg border bg-background/95 px-3 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/80'>
              {/* Metadata label, styled to match the footer row count (text-xs
                  muted) and hidden entirely below lg — at narrow widths it
                  collides with the centered Table/Chart toggle, the footer
                  repeats the row count, and aria-busy on the results surface
                  plus the spinning Refresh icon already signal revalidation.
                  nowrap + shrink-0/truncate make the timestamp yield first at
                  visible widths instead of wrapping under the toggle. */}
              <div className='hidden min-w-0 items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground lg:flex'>
                {result.status === 'ready' &&
                  (result.revalidating ? (
                    <span className='flex shrink-0 items-center gap-1'>
                      <Loader2 className='h-3.5 w-3.5 animate-spin' /> Updating
                      results…
                    </span>
                  ) : (
                    <>
                      <span className='shrink-0'>
                        {result.rows.length.toLocaleString()} rows
                      </span>
                      {result.builtAt && (
                        <span className='min-w-0 truncate'>
                          · Data as of {dataAsOfLabel(result.builtAt, todayPT)}
                        </span>
                      )}
                      {result.effectiveWindow && (
                        <span className='min-w-0 truncate'>
                          · {effectiveWindowLabel(result.effectiveWindow)}
                        </span>
                      )}
                      {/* Sits directly beside the effective-window label so the
                          two read together: this is the window, and this is
                          what the chart did with it. */}
                      {detailChartActive && (
                        <span className='min-w-0 truncate'>
                          · {DETAIL_CHART_CAPTION}
                        </span>
                      )}
                    </>
                  ))}
              </div>
              {showToggle && (
                <Tabs
                  value={chartActive ? 'bar' : 'table'}
                  onValueChange={value =>
                    setDefinition(
                      setView(definition, value === 'bar' ? 'bar' : 'table')
                    )
                  }
                >
                  <TabsList>
                    <TabsTrigger value='table'>
                      <Table2 className='mr-1 h-4 w-4' /> Table
                    </TabsTrigger>
                    <TabsTrigger value='bar'>
                      <BarChart3 className='mr-1 h-4 w-4' /> Chart
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
              <div className='flex items-center gap-2'>
                {result.status === 'ready' && (
                  <Button
                    variant='ghost'
                    size='sm'
                    disabled={result.revalidating}
                    onClick={result.refreshData}
                  >
                    <RefreshCw
                      className={
                        result.revalidating ? 'h-4 w-4 animate-spin' : 'h-4 w-4'
                      }
                    />
                    Refresh
                  </Button>
                )}
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className='inline-block'>
                        <Button
                          variant='outline'
                          size='sm'
                          disabled={!exportReady}
                          onClick={handleExport}
                        >
                          <Download className='h-4 w-4' />
                          {/* Truth in labeling: a truncated detail dump silently
                              caps the export at the row limit, so state the cap
                              AND the format (.xlsx) on the button itself —
                              reconciling a billing report off a hidden 10k cap is
                              dangerous. */}
                          {exportLabel}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {result.status !== 'ready' && (
                      <TooltipContent>Run the report to export</TooltipContent>
                    )}
                    {result.status === 'ready' && result.revalidating && (
                      <TooltipContent>Updating results…</TooltipContent>
                    )}
                    {exportReady && truncated && (
                      <TooltipContent>
                        Only the first {DETAIL_ROW_LIMIT.toLocaleString()} rows
                        are included (.xlsx)
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className='inline-block'>
                        <Button
                          size='sm'
                          // 'invalid' is the ONE runner state the definition
                          // itself causes and the user can fix (a compile error
                          // like "Sum needs a group-by"), so saving it would
                          // persist a report that can never run — and, shared to
                          // the agency, can never run for anyone. Every other
                          // non-ready state is transient or environmental
                          // (fetch/build failure, unsupported browser, too-large
                          // extract, too-wide pivot); blocking Save there would
                          // strand the user's work behind a hiccup they cannot
                          // fix from this screen.
                          disabled={
                            !definition ||
                            !dataset ||
                            result.status === 'invalid'
                          }
                          onClick={() =>
                            setSaveDialog(isCopy ? 'copy' : 'save')
                          }
                        >
                          <Save className='h-4 w-4' />
                          {isCopy ? 'Save a copy' : 'Save report'}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {result.status === 'invalid' && (
                      <TooltipContent>
                        Fix the report before saving
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                {dataset && definition && (
                  <ReportPopulationControls
                    includeTestRows={populationSettingEnabled(
                      definition,
                      'include_test_rows'
                    )}
                    includeInactiveEpisodes={populationSettingEnabled(
                      definition,
                      'include_inactive_episodes'
                    )}
                    onIncludeTestRowsChange={checked =>
                      setDefinition(
                        setPopulationSetting(
                          definition,
                          'include_test_rows',
                          checked
                        )
                      )
                    }
                    onIncludeInactiveEpisodesChange={checked =>
                      setDefinition(
                        setPopulationSetting(
                          definition,
                          'include_inactive_episodes',
                          checked
                        )
                      )
                    }
                  />
                )}
              </div>
            </div>
          )}
          {/* aria-busy marks the results surface as updating while a definition
              edit re-runs, replacing the old floating inline spinners. */}
          <div aria-busy={result.revalidating}>
            {result.status === 'idle' && (
              <div className='flex min-h-64 flex-col items-center justify-center rounded-lg border border-dashed border-border p-8 text-center'>
                <BarChart3
                  className='mb-3 h-8 w-8 text-muted-foreground/60'
                  aria-hidden
                />
                <p className='mb-4 text-sm font-medium'>
                  Build a report in three steps
                </p>
                <ol className='space-y-1 text-sm text-muted-foreground'>
                  <li>
                    <span className='font-medium text-foreground'>1.</span>{' '}
                    Choose your data
                  </li>
                  <li>
                    <span className='font-medium text-foreground'>2.</span>{' '}
                    Group, filter, and sort it
                  </li>
                  <li>
                    <span className='font-medium text-foreground'>3.</span> View
                    it as a table or chart
                  </li>
                </ol>
              </div>
            )}
            {result.status === 'loading' &&
              (result.buildingProgress ? (
                <div
                  className='flex min-h-64 flex-col items-center justify-center gap-2 rounded-lg border border-border bg-muted/20 p-8 text-center'
                  role='status'
                  aria-busy='true'
                  aria-live='polite'
                >
                  <Loader2 className='h-8 w-8 animate-spin text-primary' />
                  <p className='text-sm font-medium'>Preparing your report…</p>
                  {result.buildingProgress.slow && (
                    <p className='text-sm text-amber-700'>
                      This is taking longer than usual.
                    </p>
                  )}
                  <p className='text-sm text-muted-foreground'>
                    {Math.floor(result.buildingProgress.elapsedMs / 1000)}{' '}
                    seconds elapsed
                  </p>
                </div>
              ) : (
                <div
                  className='flex min-h-64 flex-col items-center justify-center gap-2 rounded-lg border border-border bg-muted/20 p-8 text-center'
                  role='status'
                  aria-busy='true'
                  aria-live='polite'
                >
                  <Loader2 className='h-8 w-8 animate-spin text-primary' />
                  <p className='text-sm font-medium'>Loading report…</p>
                </div>
              ))}
            {result.status === 'unsupported' && (
              <Alert>
                <AlertTriangle className='h-4 w-4' />
                <AlertDescription>
                  Reporting isn't supported on this browser.
                </AlertDescription>
              </Alert>
            )}
            {result.status === 'too-large' && (
              <Alert>
                <AlertTriangle className='h-4 w-4' />
                <AlertDescription>
                  This report is too large — narrow the date range.
                </AlertDescription>
              </Alert>
            )}
            {result.status === 'pivot-too-wide' && (
              <Alert>
                <AlertTriangle className='h-4 w-4' />
                <AlertDescription>
                  Too many values to pivot — filter first.
                </AlertDescription>
              </Alert>
            )}
            {result.status === 'invalid' && (
              <Alert>
                <AlertTriangle className='h-4 w-4' />
                <AlertDescription>
                  {result.message ??
                    'This report setup is incomplete. Adjust the fields to continue.'}
                </AlertDescription>
              </Alert>
            )}
            {result.status === 'error' && (
              <Alert>
                <AlertTriangle className='h-4 w-4' />
                <AlertDescription className='flex items-center justify-between gap-4'>
                  <span>
                    Something went wrong running this report. Please try again.
                  </span>
                  <Button size='sm' variant='outline' onClick={result.reload}>
                    Try again
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            {result.status === 'ready' && (
              <>
                {/* OUTSIDE the empty/non-empty branch, and above the numbers.
                    A restricted caller is most likely to be confused when the
                    report comes back EMPTY — that is when a narrowed scope is
                    indistinguishable from "nothing matched" — so hiding the
                    notice on the empty branch removed it from the one case it
                    was written for. It also changes how the figures should be
                    read when there ARE rows, so it cannot be a footnote. */}
                <ReportScopeNotice
                  dataset={dataset}
                  joined={[...joinedRelated.values()]}
                />
                {result.rows.length === 0 ? (
                  // A caller who may read no clinic gets the scope notice
                  // alone: "No results match your filters" would blame their
                  // filters for a door that is locked, and send them off to
                  // widen a filter that cannot help.
                  hasNoClinicAccess(dataset) ? null : (
                    <p className='py-8 text-center text-sm text-muted-foreground'>
                      No results match your filters.
                    </p>
                  )
                ) : (
                  <div className='relative'>
                    {chartActive ? (
                      <div className='space-y-2'>
                        {/* Top-N disclosure for a detail chart. The chart query is
                          bounded in SQL, so the TRUE category count arrives as a
                          scalar column rather than being inferable from the
                          rows; mirrors ResultsChart's own overflow banner. */}
                        {chartResult &&
                          chartResult.categoryTotal !== null &&
                          chartResult.categoryTotal > MAX_CATEGORIES && (
                            <p className='inline-flex w-fit items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs text-muted-foreground'>
                              <Info
                                className='h-3.5 w-3.5 shrink-0'
                                aria-hidden
                              />
                              Showing top {MAX_CATEGORIES} of{' '}
                              {chartResult.categoryTotal} categories
                            </p>
                          )}
                        <ResultsChart
                          columns={visibleColumns}
                          rows={visibleRows}
                          dimensionKeys={
                            detailChart
                              ? [detailChart.group_by]
                              : (definition?.group_bys.map(g => g.column) ?? [])
                          }
                          columnLabels={columnLabels}
                          columnMeta={columnMeta}
                          // Derived from definition.group_bys, so EMPTY for a
                          // detail chart — correct: ResultsChart's
                          // resolveFormatKind falls back to columnMeta. Do not
                          // recompute it from the derived definition.
                          dimFormats={dimFormats}
                          pivotColumn={pivotColumnName}
                        />
                      </div>
                    ) : (
                      <ResultsTable
                        columns={result.columns}
                        rows={result.rows}
                        columnLabels={columnLabels}
                        columnMeta={columnMeta}
                        dimFormats={dimFormats}
                        pivotColumn={pivotColumnName}
                        truncated={truncated}
                        // A truncated detail table beside a working chart tab
                        // must not imply the chart is capped too: the chart
                        // aggregates the whole fetched window.
                        chartComputesFullWindow={validChart}
                        updating={result.revalidating}
                        sort={definition?.sort}
                        sortableColumns={sortableColumns}
                        // Group-by dimensions drive the "None" null rendering, the
                        // merged leading-dimension cell, and the TOTAL row. Empty for
                        // a detail result, which suppresses all three.
                        groupByColumns={definition?.group_bys.map(
                          g => g.column
                        )}
                        subtotals={definition?.subtotals}
                        onSortChange={column =>
                          setDefinition(cycleSort(definition!, column))
                        }
                        // Column reorder only for a true detail result — grouped/
                        // pivot result columns are derived (group_bys then measures
                        // / pivot values), so their order isn't representable in
                        // definition.columns. reorderColumns materializes the
                        // explicit order from result.columns and persists it; the
                        // setDefinition → recompile round-trip re-renders headers in
                        // the new order (ordering still runs in DuckDB).
                        onColumnReorder={
                          isDetailResult
                            ? (from, to) =>
                                setDefinition(
                                  reorderColumns(
                                    definition!,
                                    from,
                                    to,
                                    result.columns
                                  )
                                )
                            : undefined
                        }
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      <ConfirmationDialog
        open={pendingDataset !== null}
        title='Change dataset?'
        message='This clears the current report setup.'
        cancelLabel='Cancel'
        confirmLabel='Change dataset'
        onCancel={() => setPendingDataset(null)}
        onAccept={() => {
          if (pendingDataset) applyDataset(pendingDataset);
          setPendingDataset(null);
        }}
      />

      <SaveReportDialog
        open={saveDialog !== null}
        onOpenChange={open => !open && setSaveDialog(null)}
        mode={saveDialog ?? 'copy'}
        initialName={sourceReport?.name ?? ''}
        initialDescription={sourceReport?.description ?? ''}
        // Same condition under which submit creates rather than updates in
        // place (mirrors the branch below and the Save button's isCopy label).
        showVisibility={
          saveDialog === 'copy' || !ownsSource || !sourceReport?.id
        }
        onSave={async (name, description, visibility) => {
          if (!definition || !dataset) return;
          try {
            // Re-stamp schema_version + related_versions from what the
            // definition actually references (backend enforces v2-iff-joined).
            // The in-memory `definition` state is left untouched — the server
            // response round-trips the stamps on reopen.
            const toSave = stampDefinitionVersions(definition, relatedMap);
            if (saveDialog === 'save' && ownsSource && sourceReport?.id) {
              const updated = await updateReportDefinition(sourceReport.id, {
                name,
                description,
                definition: toSave,
              });
              setSourceReport(updated);
            } else {
              const created = await createReportDefinition({
                name,
                description,
                dataset_key: dataset.key,
                definition: toSave,
                visibility: visibility ?? 'agency',
              });
              setSourceReport(created);
              // Claim the id BEFORE navigating: the URL change re-runs the load
              // effect, and this tells it the report it would fetch is already
              // the one on screen (see loadedKeyRef).
              loadedKeyRef.current = `id:${created.id}`;
              navigate(`/reports/builder?id=${created.id}`, { replace: true });
              // D9 skew guard: an old backend silently ignores an unknown
              // `visibility` field and returns 200/201 as if nothing changed.
              // The report still exists and navigation still proceeds — the
              // user is just told the private request didn't take.
              if (
                visibility === 'private' &&
                created.visibility !== 'private'
              ) {
                console.error(
                  'Report save did not honor visibility=private; backend response:',
                  created.visibility
                );
                // This message already reports BOTH facts (saved, but not
                // private), so it replaces the success toast rather than
                // stacking with it — a plain "Report saved" alongside it reads
                // as if the private request went through.
                showError(
                  'Reports',
                  'Report saved, but could not be made private.'
                );
                return;
              }
            }
            showSuccess('Report saved', name);
          } catch (error) {
            console.error('Failed to save report', error);
            showError('Reports', 'Failed to save report. Please try again.');
            throw error;
          }
        }}
      />
    </div>
  );
};

export default ReportBuilder;
